import { Line } from "react-chartjs-2";
import ChartCard from "./ChartsCard";
import { websiteVisitLineOptions } from "./ChartOptions/lineChartOptions";
import { useWebsiteCounter } from "../../hooks/useWebsiteCounter";
import { useState, useEffect } from "react";

export default function MonthlyWebsiteVisits() {
  const { years, getMonthlyCountsForYear, isLoading } = useWebsiteCounter();
  const [selectedYear, setSelectedYear] = useState("");
  const [monthlyData, setMonthlyData] = useState<any>([]);

  // Fetch available years and set default selection
  useEffect(() => {
    if (years.length > 0 && !selectedYear) {
      setSelectedYear(years[0]); // Select the first (newest) year by default
    }
  }, [years, selectedYear]);

  // Fetch monthly data when selected year changes
  useEffect(() => {
    const fetchMonthlyData = async () => {
      if (selectedYear) {
        try {
          const data: any = await getMonthlyCountsForYear(selectedYear);
          setMonthlyData(data);
        } catch (error) {
          console.error("Error fetching monthly data:", error);
          setMonthlyData(new Array(12).fill(0));
        }
      }
    };

    fetchMonthlyData();
  }, [selectedYear, getMonthlyCountsForYear]);

  // Initialize chart data structure
  const monthlyWebsiteVisitsData = {
    labels: [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ],
    datasets: [
      {
        label: "Visits",
        data: monthlyData,
        borderWidth: 2,
        tension: 0.4,
        pointRadius: 4,
        borderColor: "rgb(59, 130, 246)", // Blue border
        backgroundColor: "rgba(59, 130, 246, 0.1)", // Light blue fill
        pointBackgroundColor: "rgb(59, 130, 246)",
        pointBorderColor: "#ffffff",
        pointHoverBackgroundColor: "#ffffff",
        pointHoverBorderColor: "rgb(59, 130, 246)",
      },
    ],
  };

  // Handle year change
  const handleYearChange = (e: any) => {
    setSelectedYear(e.target.value);
  };

  if (isLoading && !selectedYear) {
    return (
      <ChartCard title="Monthly Website Visits">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading chart data...</div>
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard title="Monthly Website Visits">
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {selectedYear && (
            <div className="text-sm text-gray-600 px-3 py-2">
              <span className="font-medium">{selectedYear} Visits:</span>{" "}
              {monthlyData.reduce((sum: any, count: any) => sum + count, 0)}
            </div>
          )}
          <div className="relative w-full max-w-[90px]">
            <select
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 appearance-none pr-10"
              disabled={years.length === 0}
            >
              {years.length === 0 ? (
                <option value="">No data available</option>
              ) : (
                years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))
              )}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

        </div>
      </div>

      <div className="h-64">
        <Line
          data={monthlyWebsiteVisitsData}
          options={{
            ...websiteVisitLineOptions,
          }}
        />
      </div>

      {selectedYear && monthlyData.length > 0 && (
        <div className="mt-4 text-xs text-gray-500 text-center">
          Showing monthly visits for {selectedYear}. Hover over points for exact numbers.
        </div>
      )}
    </ChartCard>
  );
}